<template>
    <div
        v-show="showDownloadList"
        ref="draggableDownloadRef"
        class="z-100 fixed max-mob:w-full touch-none cursor-move max-mob:px-4"
        :style="currentStyles.style"
        @click.stop.prevent
    >
        <div class="w-full min-mob:w-101 bg-white dark:bg-blue-840 border border-gray-100 dark:border-blue-720 rounded-1.25">
            <div class="max-mob:p-3 p-4 flex justify-between items-center border-b border-gray-100 dark:border-blue-720">
                <div class="text-sm font-medium text-gray-370 dark:text-white">
                    Downloads
                </div>

                <CloseIconMini
                    class="text-gray-200 cursor-pointer"
                    @click="hidePopup"
                />
            </div>

            <div class="max-mob:p-3 p-4">
                <template v-for="(item, index) in downloadItems" :key="item.type">
                    <CustomDivider
                        v-if="index"
                        class="max-mob:my-3 my-4"
                        color="border-white-500 dark:border-gray-760"
                    />

                    <DownloadItem
                        :title="item.title"
                        :size="item.size"
                        :status="item.status"
                        :statuses="item.statuses"
                        @removeItem="removeItem(item.type)"
                    />
                </template>
            </div>
        </div>
    </div>

    <div
        v-show="showDownloadMobile"
        class="max-mob:flex hidden z-100 fixed left-4 bottom-4 w-14 h-14 bg-white dark:bg-blue-840 border border-gray-100 dark:border-blue-720 rounded-custom justify-center items-center"
        @click="hidePopup"
    >
        <component
            :is="currentIcon"
            class="text-gray-370 dark:text-white"
        />
    </div>
</template>

<script setup>
import {
    ref, computed, inject, watch, markRaw, useTemplateRef,
} from 'vue'
import { useDraggable, useElementSize } from '@vueuse/core'

import DownloadItem from '@/components/download/DownloadItem.vue'
import CustomDivider from '@/components/divider/CustomDivider.vue'
import CloseIconMini from '@/components/icons/CloseIconMini.vue'
import FailDownloadIcon from '@/components/icons/download/FailDownloadIcon.vue'
import AnimationDownloadIcon from '@/components/icons/download/AnimationDownloadIcon.vue'
import SuccessDownloadIcon from '@/components/icons/download/SuccessDownloadIcon.vue'

import { useDownloadReportingStore } from '@/store/download/downloadReportingStore'
import { useDownloadLeadsStore } from '@/store/download/downloadLeadsStore'

import { viewPorts } from '@/constants/viewPorts'

const props = defineProps({
    showDownloadPopup: {
        type: Boolean,
        default: false,
    },
})

const emits = defineEmits(['changeShowDownloadPopup'])

const downloadReportingStore = useDownloadReportingStore()
const downloadLeadsStore = useDownloadLeadsStore()

const currentViewSize = inject('currentViewSize')
const currentWidth = inject('currentWidth')

const draggableDownloadRef = useTemplateRef('draggableDownloadRef')

const currentStyles = ref(null)

const { height } = useElementSize(draggableDownloadRef)

const isMobView = computed(() => viewPorts.mob >= currentViewSize.value)

const types = {
    aggregate: 'aggregate',
    log: 'log',
    leads: 'leads',
}

const downloadItems = computed(() => {
    const items = []

    if (downloadReportingStore.downloadingAggregateStatus) {
        items.push({
            type: types.aggregate,
            title: downloadReportingStore.aggregateFilename,
            size: downloadReportingStore.aggregateSize,
            status: downloadReportingStore.downloadingAggregateStatus,
            statuses: downloadReportingStore.statuses,
        })
    }

    if (downloadReportingStore.downloadingLogStatus) {
        items.push({
            type: types.log,
            title: getLogFilename.value,
            size: downloadReportingStore.logSize,
            status: downloadReportingStore.downloadingLogStatus,
            statuses: downloadReportingStore.statuses,
        })
    }

    if (downloadLeadsStore.downloadingLeadsStatus) {
        items.push({
            type: types.leads,
            title: getLeadsFilename.value,
            size: downloadLeadsStore.leadsSize,
            status: downloadLeadsStore.downloadingLeadsStatus,
            statuses: downloadLeadsStore.statuses,
        })
    }

    return items
})

const allStatuses = computed(() => [
    downloadReportingStore.downloadingAggregateStatus,
    downloadReportingStore.downloadingLogStatus,
    downloadLeadsStore.downloadingLeadsStatus,
])

const hasAnyDownload = computed(() => allStatuses.value.some((status) => status !== null))

const showDownloadList = computed(() => hasAnyDownload.value && props.showDownloadPopup)

const showDownloadMobile = computed(() => hasAnyDownload.value && !props.showDownloadPopup)

const someFailed = computed(() => allStatuses.value.includes('failed'))

const someInProgress = computed(() => allStatuses.value.includes('in_process'))

const currentIcon = computed(() => {
    switch (true) {
    case someFailed.value:
        return markRaw(FailDownloadIcon)
    case someInProgress.value:
        return markRaw(AnimationDownloadIcon)
    default:
        return markRaw(SuccessDownloadIcon)
    }
})

const getLogFilename = computed(() => {
    if (downloadReportingStore.logFilename) {
        return downloadReportingStore.logFilename
    }

    if (downloadReportingStore.downloadingLogStatus) {
        return 'call-log-export.xlsx'
    }
})

const getLeadsFilename = computed(() => {
    if (downloadLeadsStore.leadsFilename) {
        return downloadLeadsStore.leadsFilename
    }

    if (downloadLeadsStore.downloadingLeadsStatus) {
        return 'leads-export.xlsx'
    }
})

function setStyles() {
    currentStyles.value = useDraggable(draggableDownloadRef, {
        initialValue: isMobView.value
            ? { x: 0, y: window.innerHeight - height.value - 24 }
            : { x: window.innerWidth - 424, y: 75 },
    })
}

function hidePopup() {
    emits('changeShowDownloadPopup')
}

function removeItem(type) {
    switch (type) {
    case types.aggregate:
        return downloadReportingStore.removeAggregateStatus()
    case types.log:
        return downloadReportingStore.removeLogStatus()
    case types.leads:
        return downloadLeadsStore.removeLeadsStatus()
    }
}

watch(() => currentWidth.value, () => {
    setStyles()
}, { immediate: true })

watch(() => height.value, () => {
    setStyles()
}, { immediate: true })
</script>
